/**
 * The ops panel — is anything broken, and who did what.
 *
 * WHY ONE QUERY RATHER THAN SIX. An operator asking "is anything broken" wants
 * one answer, and six sequential queries is six chances for the page to
 * half-render. More importantly a service being DOWN must produce a ROW saying
 * so rather than a failed query, and that only works because the BFF owns the
 * fan-out and catches each probe itself.
 *
 * The rebuild button is the sharpest thing on this page. It truncates the
 * analytics read model, so it asks first, says plainly what will happen, and
 * reports a PARTIAL rebuild as partial rather than as a failure — three
 * services replaying and one failing is a real state somebody has to
 * understand.
 */
import { Badge, Button, Card, EmptyState, Spinner } from '@meshhub/design-tokens/ui';
import { useMutation, useQuery } from '@apollo/client';
import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import { ConfirmDialog, MutationError, SelectField } from '../../components/FormKit.web.jsx';
import { formatCount, formatDateTime, toPersianDigits } from '../../lib/format.js';
import { AUDIT_LOG_QUERY, REBUILD_ANALYTICS, SYSTEM_HEALTH_QUERY } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

function ServiceRow({ service }) {
  const Icon = service.healthy ? CheckCircle2 : XCircle;
  return (
    <tr className="border-t border-border">
      <td className="p-2">
        <span
          className={`flex items-center gap-2 text-sm ${
            service.healthy ? 'text-success' : 'text-danger'
          }`}
        >
          <Icon size={14} aria-hidden="true" />
          {service.name}
        </span>
      </td>
      <td className="p-2 text-xs text-text-muted" dir="ltr">
        {service.address}
      </td>
      <td className="p-2 text-xs tabular-nums text-text-muted">
        {toPersianDigits(service.latencyMs)} ms
      </td>
      <td className="p-2 text-xs text-text-muted">
        {/* The mapper's message, which for a wrong port names BOTH causes
            worth checking rather than guessing one (CLAUDE.md §15). */}
        {service.detail}
      </td>
    </tr>
  );
}

function LagBadge({ seconds }) {
  if (seconds === null || seconds === undefined) return null;
  const stale = seconds > 600;
  return (
    <Badge className={stale ? 'bg-warning-subtle text-warning' : 'bg-surface-hover text-text-muted'}>
      {toPersianDigits(Math.round(seconds))}s
    </Badge>
  );
}

/**
 * How far behind one projection source is, in seconds.
 *
 * `lastEventAt` is when the producer emitted; `lastAppliedAt` is when this
 * service folded it in. The gap between them is the lag — and when nothing has
 * arrived at all there is no lag to report rather than a zero, because zero
 * reads as «up to date» for a source that may simply be dead.
 */
function lagSeconds(source) {
  if (!source?.lastEventAt || !source?.lastAppliedAt) return null;
  const gap =
    (new Date(source.lastAppliedAt).getTime() - new Date(source.lastEventAt).getTime()) /
    1000;
  return gap > 0 ? Math.round(gap) : 0;
}

export function SystemScreen() {
  const [confirmRebuild, setConfirmRebuild] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);
  const [auditActor, setAuditActor] = useState('');
  const [auditService, setAuditService] = useState('');

  const { data, loading, error, refetch } = useQuery(SYSTEM_HEALTH_QUERY, {
    // Polled, like the reminder tray and for the same reason: this is not a
    // real-time surface, and a socket per open tab is worse than a query a
    // minute (todo.md).
    pollInterval: 30_000,
    fetchPolicy: 'cache-and-network',
  });

  const audit = useQuery(AUDIT_LOG_QUERY, {
    variables: {
      page: 1,
      pageSize: 50,
      actorId: auditActor || null,
      services: auditService ? [auditService] : null,
    },
  });

  const [rebuild, rebuildState] = useMutation(REBUILD_ANALYTICS, {
    onCompleted: (d) => {
      setRebuildResult(d.rebuildAnalytics);
      refetch();
    },
  });

  const health = data?.systemHealth;

  return (
    <div className="flex flex-col gap-4 p-4">
      <header className="flex items-center gap-3">
        <h1 className="flex-1 text-lg font-bold text-text">{S.ops.title}</h1>
        {health && (
          <span className="text-xs text-text-muted">
            {S.ops.checkedAt}: {formatDateTime(health.checkedAt)}
          </span>
        )}
        <Button variant="ghost" onClick={() => refetch()}>
          <RefreshCw size={14} aria-hidden="true" />
          {S.dashboard.refresh}
        </Button>
      </header>

      {loading && !data && <Spinner />}
      {error && <EmptyState title={error.message} />}

      {health && (
        <>
          <Card className="p-2">
            <h2 className="p-2 text-sm font-bold text-text">{S.ops.services}</h2>
            <table className="w-full border-collapse">
              <thead className="text-xs text-text-muted">
                <tr>
                  <th className="p-2 text-start font-medium">{S.ops.services}</th>
                  <th className="p-2 text-start font-medium">{S.ops.address}</th>
                  <th className="p-2 text-start font-medium">{S.ops.latency}</th>
                  <th className="p-2 text-start font-medium">{S.ops.details}</th>
                </tr>
              </thead>
              <tbody>
                {health.services.map((s) => (
                  <ServiceRow key={s.name} service={s} />
                ))}
              </tbody>
            </table>
          </Card>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Card className="flex flex-col gap-2 p-4">
              <h2 className="text-sm font-bold text-text">{S.ops.ingest}</h2>
              <table className="w-full border-collapse text-sm">
                <thead className="text-xs text-text-muted">
                  <tr>
                    <th className="p-1 text-start font-medium">{S.ops.exchange}</th>
                    <th className="p-1 text-start font-medium">{S.ops.lag}</th>
                    <th className="p-1 text-start font-medium">{S.ops.applied}</th>
                    <th className="p-1 text-start font-medium" title={S.ops.unknownHint}>
                      {S.ops.unknown}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(health.analytics?.exchanges ?? []).map((e) => (
                    <tr key={e.exchange} className="border-t border-border">
                      <td className="p-1 text-xs" dir="ltr">
                        {e.exchange}
                      </td>
                      <td className="p-1">
                        <LagBadge seconds={e.lagSeconds} />
                      </td>
                      <td className="p-1 tabular-nums text-xs">{formatCount(e.appliedCount)}</td>
                      <td
                        className={`p-1 tabular-nums text-xs ${
                          e.unknownCount > 0 ? 'text-warning' : 'text-text-muted'
                        }`}
                      >
                        {formatCount(e.unknownCount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <Card className="flex flex-col gap-2 p-4">
              <h2 className="text-sm font-bold text-text">{S.ops.projections}</h2>
              {health.projections && (
                <dl className="grid grid-cols-2 gap-1 text-sm">
                  <dt className="text-text-muted">{S.ops.recipients}</dt>
                  <dd className="tabular-nums">{formatCount(health.projections.recipientCount)}</dd>
                  {/* PER SOURCE, because that is the shape the server sends and
                      the only place the lag exists. This panel used to read
                      `templateCount` and `lagSeconds` off ProjectionStatus —
                      fields it has never had — which made the whole
                      systemHealth query invalid, so nothing here rendered at
                      all. Each row is one exchange this service folds in, and a
                      single one falling behind is exactly what the panel is for. */}
                  {(health.projections.sources ?? []).map((source) => (
                    <React.Fragment key={source.source}>
                      <dt className="text-text-muted">{source.source}</dt>
                      <dd>
                        <LagBadge seconds={lagSeconds(source)} />
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
              {health.whatsapp && (
                <p className="border-t border-border pt-2 text-sm">
                  {S.ops.whatsapp}:{' '}
                  {/* «sandbox» and «connected» must never be confusable — the
                      one rule the messaging surface has above all others. */}
                  <Badge
                    className={
                      health.whatsapp.provider === 'fake'
                        ? 'bg-warning-subtle text-warning'
                        : 'bg-surface-hover text-text'
                    }
                  >
                    {health.whatsapp.provider === 'fake'
                      ? S.ops.sandbox
                      : S.ops.live}
                  </Badge>
                </p>
              )}
            </Card>
          </div>

          <Card className="flex flex-col gap-2 p-4">
            <h2 className="text-sm font-bold text-text">{S.ops.rebuild}</h2>
            <p className="text-xs text-text-muted">{S.ops.rebuildWarning}</p>
            {health.analytics?.rebuildRunning && (
              <Badge className="w-fit bg-warning-subtle text-warning">
                {S.ops.rebuildRunning}
              </Badge>
            )}
            <Button
              variant="secondary"
              className="w-fit"
              disabled={rebuildState.loading}
              onClick={() => setConfirmRebuild(true)}
            >
              {rebuildState.loading ? <Spinner /> : <RefreshCw size={14} aria-hidden="true" />}
              {S.ops.rebuild}
            </Button>
            <MutationError error={rebuildState.error} />
            {rebuildState.error && (
              <p className="flex items-start gap-2 text-xs text-warning">
                <AlertTriangle size={13} className="mt-px shrink-0" aria-hidden="true" />
                {S.ops.rebuildPartial}
              </p>
            )}
            {rebuildResult && (
              <p className="text-xs text-success">
                {S.ops.rebuildDone} — {S.ops.replayed}:{' '}
                <span dir="ltr">{rebuildResult.replayedJson}</span>
              </p>
            )}
          </Card>

          <Card className="p-2">
            <div className="flex flex-wrap items-center gap-2 p-2">
              <h2 className="flex-1 text-sm font-bold text-text">{S.ops.audit}</h2>
              <SelectField
                label={S.ops.auditService}
                value={auditService}
                onChange={(e) => setAuditService(e.target.value)}
              >
                <option value="">{S.app.all}</option>
                {['crm', 'scheduling', 'messaging', 'feedback'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </SelectField>
            </div>
            {audit.loading && <Spinner />}
            {audit.data?.auditLog?.items?.length === 0 && (
              <EmptyState title={S.ops.auditEmpty} />
            )}
            {audit.data?.auditLog?.items?.length > 0 && (
              <table className="w-full border-collapse text-xs">
                <thead className="text-text-muted">
                  <tr>
                    <th className="p-2 text-start font-medium">{S.ops.auditWhen}</th>
                    <th className="p-2 text-start font-medium">{S.ops.auditService}</th>
                    <th className="p-2 text-start font-medium">{S.ops.auditActor}</th>
                    <th className="p-2 text-start font-medium">{S.ops.auditAction}</th>
                    <th className="p-2 text-start font-medium">{S.ops.auditEntity}</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.data.auditLog.items.map((row) => (
                    <tr key={row.id} className="border-t border-border">
                      <td className="whitespace-nowrap p-2 text-text-muted">
                        {formatDateTime(row.occurredAt)}
                      </td>
                      <td className="p-2" dir="ltr">
                        {row.service}
                      </td>
                      <td className="p-2">
                        {/* A raw id where a name should be is what the
                            app_user replay exists to prevent. */}
                        {row.actorName || (
                          <span className="text-text-muted" dir="ltr">
                            {row.actorId.slice(0, 8)}
                          </span>
                        )}
                      </td>
                      <td className="p-2" dir="ltr">
                        {row.action}
                      </td>
                      <td className="p-2 text-text-muted" dir="ltr">
                        {/* Empty rather than synthetic: not every audited
                            action is about one entity (CLAUDE.md §15). */}
                        {row.entityId ? row.entityId.slice(0, 8) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      <ConfirmDialog
        open={confirmRebuild}
        onOpenChange={setConfirmRebuild}
        title={S.ops.rebuild}
        message={S.ops.rebuildWarning}
        confirmLabel={S.ops.rebuildConfirm}
        onConfirm={() => {
          setRebuildResult(null);
          rebuild({ variables: { includeDimensions: true } });
          setConfirmRebuild(false);
        }}
      />
    </div>
  );
}
